/**
 * MarkerManager - Manages timeline markers
 * Handles creation, rendering, and interaction with timeline markers
 */
class MarkerManager {
  constructor(timeline) {
    this.timeline = timeline;
    this.isMarkerMode = false;
    this.draggedMarker = null;
    this.markerInitialDay = 0;
    this.markerInitialRowIndex = 0;
    this.hasNewMarkers = false;
    this.isCreatingMarker = false; // Flag to prevent rapid marker creation

    // Enhanced dragging properties
    this.isDragging = false;
    this.dragStartX = 0;
    this.dragStartY = 0;
    this.initialMarkerPosition = null;
    this.dragThreshold = 5; // Minimum pixels to start dragging

    // Bind methods for event listeners
    this.handleMarkerMouseDown = this.handleMarkerMouseDown.bind(this);
    this.handleMarkerMouseMove = this.handleMarkerMouseMove.bind(this);
    this.handleMarkerMouseUp = this.handleMarkerMouseUp.bind(this);
    this.handleMarkerTouchStart = this.handleMarkerTouchStart.bind(this);
    this.handleMarkerTouchMove = this.handleMarkerTouchMove.bind(this);
    this.handleMarkerTouchEnd = this.handleMarkerTouchEnd.bind(this);
  }

  /**
   * Add a marker at a specific day and row
   * @param {number} day - Day number
   * @param {number} rowIndex - Row index
   * @param {Object} extraData - More information of Marker
   */
  async addMarker(day, rowIndex, extraData = {}) {
    // Prevent rapid marker creation
    if (this.isCreatingMarker) {
      console.warn('Marker creation already in progress');
      return false;
    }

    this.isCreatingMarker = true;

    try {
      const marker = {
        id: null,
        tempId: Date.now(),
        day: day,
        // date คือค่าที่บันทึกลงฐานข้อมูล ส่วน day เป็นเพียงดัชนีของ header
        // ซึ่งเปลี่ยนความหมายทุกครั้งที่ช่วงวันที่ (filterRange) เปลี่ยน
        date: this.timeline.workingDaysManager.markerDayToDate(day) || null,
        rowIndex: rowIndex,
        color: this.timeline.currentColor,
        name: extraData.name || '',
        description: extraData.description || ''
      };

      this.timeline.dataManager.markers.push(marker);
      this.renderMarker(marker);
      this.hasNewMarkers = true;

      this.isMarkerMode = false;
      const markerModeBtn = document.getElementById('toggle-marker-mode');
      if (markerModeBtn) {
        markerModeBtn.textContent = this.timeline.t('Add marker');
      }

      // ส่งข้อมูล marker ไปยัง API ทันที
      const result = await this.sendMarkerToAPI(marker);

      if (result.success) {
        // Re-render marker เพื่ออัปเดต event listeners ด้วย ID ใหม่
        this.renderMarker(marker);
        this.timeline.showNotification(this.timeline.t('Marker added successfully'), 'success');
      } else {
        this.timeline.showNotification(
          this.timeline.t('Failed to add marker to server: {message}', {message: result.error || 'Unknown error'}),
          'error'
        );
      }

      this.timeline.triggerCallback('onMarkerCreate', marker);
      this.timeline.triggerDataChangeCallback();

      return result.success;
    } finally {
      // Reset flag after operation
      setTimeout(() => {
        this.isCreatingMarker = false;
      }, 500);
    }
  }

  /**
   * Resets the flag indicating the presence of new markers.
   * Sets the hasNewMarkers property to false.
   */
  resetNewMarkersFlag() {
    this.hasNewMarkers = false;
  }

  /**
   * Render all markers
   */
  renderMarkers() {
    document.querySelectorAll('.timeline-marker').forEach(el => el.remove());

    this.timeline.dataManager.markers.forEach(marker => {
      // ดัชนี header ต้องคำนวณใหม่ทุกครั้งที่ render เพราะ index 0 ผูกกับ
      // filterRange.start ซึ่งเปลี่ยนได้ (และ API ส่งมาแต่ date ไม่มี day)
      this.syncMarkerDate(marker);
      this.renderMarker(marker);
    });
  }

  /**
   * ทำให้ marker.day (ดัชนีของ header) กับ marker.date (วันที่จริง) ตรงกัน
   * โดยยึด date เป็นหลักถ้ามี มิฉะนั้นจึงคำนวณ date จาก day
   * @param {Object} marker
   * @returns {Object} marker เดิมที่ถูกปรับค่าแล้ว
   */
  syncMarkerDate(marker) {
    const wdm = this.timeline.workingDaysManager;
    if (!wdm) return marker;

    if (marker.date) {
      const day = wdm.dateToMarkerDay(marker.date);
      if (day >= 0) {
        marker.day = day;
      } else {
        // วันของ marker อาจหลุดออกจากตารางวันทำงาน (เช่นวันนั้นเพิ่งถูกตั้งเป็น
        // วันหยุด หรือถูกกรองออกด้วยช่วงวันที่) ให้เกาะวันที่ใกล้ที่สุดแทนการตกไปที่ 0
        marker.day = wdm.calculateBLockIndex(marker.date);
      }
    } else if (typeof marker.day === 'number' && !isNaN(marker.day)) {
      marker.date = wdm.markerDayToDate(marker.day) || null;
    }

    return marker;
  }

  /**
   * Enhanced marker rendering with proper event setup
   */
  renderMarker(marker) {
    // Remove existing marker if any
    const existingMarker = document.querySelector(`.timeline-marker[data-id="${marker.id || marker.tempId}"]`);
    if (existingMarker) {
      existingMarker.remove();
    }

    if (!this.timeline.timelineContainer) return;

    const dayWidth = this.timeline.options.itemWidth || 30;

    // Calculate position using WorkingDaysManager
    let left = this.calculateMarkerPosition(marker, dayWidth);

    const markerElement = document.createElement('div');
    markerElement.className = 'timeline-marker draggable-marker';
    markerElement.dataset.id = marker.id || marker.tempId;
    markerElement.dataset.day = marker.day;
    markerElement.dataset.rowIndex = marker.rowIndex || 0;
    markerElement.style.left = `${left}px`;
    if (marker.name) {
      markerElement.title = marker.name;
    }

    const tasksArea = this.timeline.tasksArea;
    if (tasksArea) {
      // offsetTop วัดจาก .timeline-container (offsetParent เพราะเป็น position:relative)
      // โดยตรง จึงรวมความสูงของ .day-headers ไว้แล้วเสมอ
      // ห้ามคำนวณจาก getBoundingClientRect() เพราะเป็นพิกัด viewport ที่เพี้ยนทันที
      // ที่ container เลื่อนแนวตั้ง หรือ .day-headers (sticky) ถูกตรึงไว้ด้านบน
      markerElement.style.top = `${tasksArea.offsetTop}px`;
      markerElement.style.height = `${tasksArea.offsetHeight}px`;
    }

    // Create marker line
    const markerLine = document.createElement('div');
    markerLine.className = 'marker-line';
    markerLine.style.borderLeftWidth = `${this.timeline.options.markerThickness || 2}px`;
    markerLine.style.borderLeftColor = marker.color || '#ff4d4d';

    // Create marker head (drag handle)
    const markerDiamond = document.createElement('div');
    markerDiamond.className = 'marker-diamond';
    markerDiamond.style.backgroundColor = marker.color || '#ff4d4d';

    // ไอคอนของ marker ถูกบันทึกไว้ในฐานข้อมูลและแก้ได้จาก slideout
    // แต่เดิมไม่เคยถูกวาด จึงมองไม่เห็นความต่างระหว่าง marker แต่ละอัน
    if (marker.icon) {
      markerDiamond.classList.add('has-icon');
      const icon = document.createElement('span');
      icon.className = `marker-icon icon-${marker.icon} notext`;
      markerDiamond.appendChild(icon);
    }

    // Add enhanced event listeners
    this.addMarkerEventListeners(markerElement, markerDiamond, marker);

    // Assemble marker
    markerElement.appendChild(markerLine);
    markerElement.appendChild(markerDiamond);

    // Add to timeline container
    this.timeline.timelineContainer.appendChild(markerElement);

    // ต้องจัดตำแหน่งหลังต่อเข้า DOM แล้วเท่านั้น ไม่งั้น offsetHeight ของหัว marker
    // ยังเป็น 0 และการจัดกึ่งกลางในแถวจะคลาด
    this.positionMarkerDiamond(markerDiamond, marker);
  }

  /**
   * Calculate marker position using WorkingDaysManager or fallback
   */
  calculateMarkerPosition(marker, dayWidth) {
    const wdm = this.timeline.workingDaysManager;

    if (wdm && wdm.initialized) {
      const markerDate = marker.date || wdm.markerDayToDate(marker.day);
      if (markerDate) {
        const left = wdm.dateToPosition(markerDate, dayWidth);
        if (left !== null && !isNaN(left)) {
          return left;
        }
      }
    }

    // Fallback calculation using 0-based index. marker.day มาจากฐานข้อมูลไม่ได้
    // จึงอาจเป็น undefined ต้องกันไว้ ไม่งั้นได้ left = NaN แล้ว marker หายไปทั้งตัว
    const day = Number(marker.day);

    return isNaN(day) ? 0 : day * dayWidth;
  }

  /**
   * Position marker diamond based on row index
   */
  positionMarkerDiamond(markerDiamond, marker) {
    const size = this.markerHeadSize(markerDiamond);
    const row = this.timeline.options.lockMarkerToTopRow ? null : this.getTaskRow(marker.rowIndex || 0);

    if (!row) {
      markerDiamond.style.top = `${-Math.round(size / 2)}px`;
      return;
    }

    // .task-row วัด offsetTop จาก .tasks-area (position:relative) ซึ่งเป็นจุดเดียวกับ
    // ที่ marker element เริ่มพอดี จึงไม่ต้องชดเชยความสูงของ .day-headers หรือของ
    // .task-group-header ที่คั่นระหว่างกลุ่มเอง — ค่าที่ได้ตรงกับแถวเสมอ
    markerDiamond.style.top = `${row.offsetTop + (row.offsetHeight - size) / 2}px`;
  }

  /**
   * หาแถวของ timeline ตาม rowIndex (ไม่ใช่ลำดับใน DOM)
   * @param {number} rowIndex
   * @returns {HTMLElement|null}
   */
  getTaskRow(rowIndex) {
    if (!this.timeline.tasksArea) return null;

    return this.timeline.tasksArea.querySelector(`.task-row[data-row-index="${rowIndex}"]`);
  }

  /**
   * รายการแถวทั้งหมด จำกัดขอบเขตไว้ที่ timeline นี้เท่านั้น
   * @returns {HTMLElement[]}
   */
  getTaskRows() {
    if (!this.timeline.tasksArea) return [];

    return Array.from(this.timeline.tasksArea.querySelectorAll('.task-row'));
  }

  /**
   * ขนาดของหัว marker อ่านจาก CSS จริงเพื่อให้การจัดกึ่งกลางตรงเสมอ
   * แม้ธีมจะปรับขนาดไอคอน
   * @param {HTMLElement} markerDiamond
   * @returns {number}
   */
  markerHeadSize(markerDiamond) {
    const measured = markerDiamond && markerDiamond.offsetHeight;

    return measured || (markerDiamond && markerDiamond.classList.contains('has-icon') ? 22 : 12);
  }

  /**
   * Add comprehensive event listeners to marker
   */
  addMarkerEventListeners(markerElement, markerDiamond, marker) {
    // Mouse events
    markerDiamond.addEventListener('mousedown', (e) => this.handleMarkerMouseDown(e, marker));

    // Touch events for mobile
    markerDiamond.addEventListener('touchstart', (e) => this.handleMarkerTouchStart(e, marker), {passive: false});

    // Double click for editing with debouncing
    let doubleClickTimeout = null;
    markerDiamond.addEventListener('dblclick', (e) => {
      e.preventDefault();
      e.stopPropagation();

      // Prevent rapid double-clicks
      if (doubleClickTimeout) {
        clearTimeout(doubleClickTimeout);
      }

      doubleClickTimeout = setTimeout(() => {
        // Make sure marker has a valid ID before editing
        const currentMarker = this.timeline.dataManager.markers.find(m =>
          (m.id && m.id == marker.id) || (m.tempId && m.tempId == marker.tempId)
        );

        if (currentMarker && (currentMarker.id || currentMarker.tempId)) {
          console.log('Double-click edit marker:', currentMarker);
          this.timeline.editMarker(currentMarker);
        } else {
          console.warn('Cannot edit marker - marker not found or has no valid ID');
          this.timeline.showNotification('Please wait for marker to be saved before editing', 'warning');
        }
        doubleClickTimeout = null;
      }, 150); // Small delay to prevent rapid clicks
    });

    // Hover events for tooltip
    markerDiamond.addEventListener('mouseover', (e) => this.showMarkerTooltip(e, marker));
    markerDiamond.addEventListener('mouseout', () => this.timeline.domRenderer.hideTooltip());

    // Prevent context menu
    markerDiamond.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  /**
   * Handle mouse down on a marker
   * @param {MouseEvent} e - Mouse event
   * @param {Object} marker - Marker data
   */
  handleMarkerMouseDown(e, marker) {
    e.preventDefault();
    e.stopPropagation();

    if (this.timeline.connectionManager.isConnectionMode) {
      return; // Don't drag in connection mode
    }

    this.startMarkerDrag(e, marker, 'mouse');
  }

  /**
   * Enhanced touch start handler
   */
  handleMarkerTouchStart(e, marker) {
    e.preventDefault();
    e.stopPropagation();

    if (this.timeline.connectionManager.isConnectionMode) {
      return;
    }

    this.startMarkerDrag(e, marker, 'touch');
  }

  /**
   * Start marker dragging operation
   */
  startMarkerDrag(e, marker, inputType) {
    this.isDragging = false; // Will be set to true when threshold is exceeded
    this.draggedMarker = marker;
    this.markerInitialDay = marker.day;
    this.markerInitialRowIndex = marker.rowIndex || 0;

    // Get initial coordinates
    const coords = this.getEventCoordinates(e, inputType);
    this.dragStartX = coords.clientX;
    this.dragStartY = coords.clientY;

    // Store initial marker position
    this.initialMarkerPosition = {
      day: marker.day,
      date: marker.date,
      rowIndex: marker.rowIndex || 0,
      left: this.calculateMarkerPosition(marker, this.timeline.options.itemWidth)
    };

    // Add global event listeners
    if (inputType === 'mouse') {
      document.addEventListener('mousemove', this.handleMarkerMouseMove);
      document.addEventListener('mouseup', this.handleMarkerMouseUp);
    } else {
      document.addEventListener('touchmove', this.handleMarkerTouchMove, {passive: false});
      document.addEventListener('touchend', this.handleMarkerTouchEnd);
    }

    // Add visual feedback
    const markerElement = document.querySelector(`.timeline-marker[data-id="${marker.id || marker.tempId}"]`);
    if (markerElement) {
      markerElement.style.cursor = 'grabbing';
      markerElement.style.userSelect = 'none';
    }

    // Prevent text selection
    document.body.style.userSelect = 'none';
  }

  /**
   * Enhanced mouse move handler
   */
  handleMarkerMouseMove(e) {
    e.preventDefault();
    this.handleMarkerDragMove(e, 'mouse');
  }

  /**
   * Enhanced touch move handler
   */
  handleMarkerTouchMove(e) {
    e.preventDefault();
    this.handleMarkerDragMove(e, 'touch');
  }

  /**
   * Handle marker drag movement
   */
  handleMarkerDragMove(e, inputType) {
    if (!this.draggedMarker) return;

    const coords = this.getEventCoordinates(e, inputType);
    const deltaX = coords.clientX - this.dragStartX;
    const deltaY = coords.clientY - this.dragStartY;

    // Check if drag threshold is exceeded
    if (!this.isDragging) {
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      if (distance < this.dragThreshold) {
        return;
      }
      this.isDragging = true;

      // Add dragging class
      const markerElement = document.querySelector(`.timeline-marker[data-id="${this.draggedMarker.id || this.draggedMarker.tempId}"]`);
      if (markerElement) {
        markerElement.classList.add('dragging');
      }
    }

    this.updateMarkerPosition(deltaX, deltaY);
  }

  /**
   * Update marker position during drag
   */
  updateMarkerPosition(deltaX, deltaY) {
    const markerElement = document.querySelector(`.timeline-marker[data-id="${this.draggedMarker.id || this.draggedMarker.tempId}"]`);
    if (!markerElement) return;

    const dayWidth = this.timeline.options.itemWidth;

    // Calculate new day position
    let newDay = this.calculateNewDay(deltaX, dayWidth);

    // Calculate new row position if not locked
    let newRowIndex = this.markerInitialRowIndex;
    if (!this.timeline.options.lockMarkerToTopRow) {
      newRowIndex = this.calculateNewRowIndex(deltaY);
    }

    // Apply constraints
    newDay = this.constrainDay(newDay);
    newRowIndex = this.constrainRowIndex(newRowIndex);

    // Update marker data
    this.draggedMarker.day = newDay;
    this.draggedMarker.date = this.timeline.workingDaysManager.markerDayToDate(newDay) || this.draggedMarker.date;
    this.draggedMarker.rowIndex = newRowIndex;

    // Update visual position
    this.updateMarkerVisualPosition(markerElement, newDay, newRowIndex);
  }

  /**
   * Calculate new day based on horizontal movement
   */
  calculateNewDay(deltaX, dayWidth) {
    let newDay;

    if (this.timeline.workingDaysManager && this.timeline.workingDaysManager.initialized) {
      // Use WorkingDaysManager for accurate calculation
      const dayMove = Math.round(deltaX / dayWidth);
      const originalDate = this.timeline.workingDaysManager.markerDayToDate(this.markerInitialDay);

      if (originalDate) {
        const newDate = this.timeline.workingDaysManager.addDays(originalDate, dayMove);
        if (newDate) {
          newDay = this.timeline.workingDaysManager.dateToMarkerDay(newDate);
        } else {
          // Fallback calculation
          newDay = this.markerInitialDay + dayMove;
        }
      } else {
        newDay = this.markerInitialDay + Math.round(deltaX / dayWidth);
      }
    } else {
      // Simple calculation
      newDay = this.markerInitialDay + Math.round(deltaX / dayWidth);
    }

    return newDay;
  }

  /**
   * Calculate new row index based on vertical movement
   */
  calculateNewRowIndex(deltaY) {
    const taskRows = this.getTaskRows();
    if (taskRows.length === 0) {
      return this.markerInitialRowIndex;
    }

    const rowHeight = taskRows[0].offsetHeight || 40;
    const rowMove = Math.round(deltaY / rowHeight);
    return this.markerInitialRowIndex + rowMove;
  }

  /**
   * Constrain day within valid range
   */
  constrainDay(day) {
    const minDay = 0;
    const maxDay = this.timeline.headers ? this.timeline.headers.length - 1 : 30;
    return Math.max(minDay, Math.min(day, maxDay));
  }

  /**
   * Constrain row index within valid range
   */
  constrainRowIndex(rowIndex) {
    const taskRows = this.getTaskRows();
    const maxRowIndex = taskRows.length > 0 ? taskRows.length - 1 : 0;

    return Math.max(0, Math.min(rowIndex, maxRowIndex));
  }

  /**
   * Update marker visual position
   */
  updateMarkerVisualPosition(markerElement, newDay, newRowIndex) {
    const dayWidth = this.timeline.options.itemWidth;

    // Calculate new left position
    let newLeft;
    if (this.timeline.workingDaysManager && this.timeline.workingDaysManager.initialized) {
      const newDate = this.timeline.workingDaysManager.markerDayToDate(newDay);
      newLeft = this.timeline.workingDaysManager.dateToPosition(newDate, dayWidth);
      if (newLeft === null) {
        newLeft = newDay * dayWidth;
      }
    } else {
      newLeft = newDay * dayWidth;
    }

    // Update marker position
    markerElement.style.left = `${newLeft}px`;
    markerElement.dataset.day = newDay;
    markerElement.dataset.rowIndex = newRowIndex;

    // Update diamond position for row change
    if (!this.timeline.options.lockMarkerToTopRow) {
      const diamond = markerElement.querySelector('.marker-diamond');
      if (diamond) {
        this.positionMarkerDiamond(diamond, {rowIndex: newRowIndex});
      }
    }
  }

  /**
   * Enhanced mouse up handler
   */
  handleMarkerMouseUp(e) {
    this.finishMarkerDrag(e, 'mouse');
  }

  /**
   * Enhanced touch end handler
   */
  handleMarkerTouchEnd(e) {
    this.finishMarkerDrag(e, 'touch');
  }

  /**
   * Finish marker dragging operation
   */
  async finishMarkerDrag(e, inputType) {
    // Remove global event listeners
    if (inputType === 'mouse') {
      document.removeEventListener('mousemove', this.handleMarkerMouseMove);
      document.removeEventListener('mouseup', this.handleMarkerMouseUp);
    } else {
      document.removeEventListener('touchmove', this.handleMarkerTouchMove);
      document.removeEventListener('touchend', this.handleMarkerTouchEnd);
    }

    // Restore cursor and selection
    document.body.style.userSelect = '';
    const markerElement = document.querySelector(`.timeline-marker[data-id="${this.draggedMarker?.id || this.draggedMarker?.tempId}"]`);
    if (markerElement) {
      markerElement.style.cursor = 'move';
      markerElement.classList.remove('dragging');
    }

    // Process the drag result
    if (this.isDragging && this.draggedMarker) {
      await this.processMarkerDragResult();
    }

    // Clean up
    this.isDragging = false;
    this.draggedMarker = null;
    this.initialMarkerPosition = null;
    this.dragStartX = 0;
    this.dragStartY = 0;
    this.markerInitialDay = 0;
    this.markerInitialRowIndex = 0;
  }

  /**
   * Process marker drag result and update server
   */
  async processMarkerDragResult() {
    if (!this.draggedMarker || !this.initialMarkerPosition) {
      return;
    }

    const hasChanged = (
      this.draggedMarker.day !== this.initialMarkerPosition.day ||
      this.draggedMarker.rowIndex !== this.initialMarkerPosition.rowIndex
    );

    if (!hasChanged) return;

    try {
      // Find the marker in the data manager
      const originalMarker = this.timeline.dataManager.markers.find(
        m => (this.draggedMarker.id && m.id === this.draggedMarker.id) ||
          (this.draggedMarker.tempId && m.tempId === this.draggedMarker.tempId)
      );

      if (originalMarker) {
        // Update marker data
        originalMarker.day = this.draggedMarker.day;
        originalMarker.date = this.draggedMarker.date;
        originalMarker.rowIndex = this.draggedMarker.rowIndex;

        // Send to API
        const result = await this.sendMarkerToAPI(originalMarker);

        if (result.success) {
          this.timeline.showNotification(this.timeline.t('Marker updated successfully'), 'success');

          // Trigger callbacks
          this.timeline.triggerCallback('onMarkerMove', originalMarker);
          this.timeline.triggerDataChangeCallback();
        } else {
          // Revert on failure
          await this.revertMarkerPosition(originalMarker);
          this.timeline.showNotification(
            this.timeline.t('Failed to update marker: {message}', {message: result.error || 'Unknown error'}),
            'error'
          );
        }
      }
    } catch (error) {
      console.error('Error processing marker drag result:', error);
      this.timeline.showNotification(
        this.timeline.t('Error updating marker position'),
        'error'
      );
    }
  }

  /**
   * Handle marker dragging
   * @param {MouseEvent} e - Mouse event
   */
  handleMarkerDragging(e) {
    if (!this.draggedMarker) return;

    const deltaX = e.clientX - this.startX;

    const dayWidth = this.timeline.options.itemWidth;
    const markerElement = document.querySelector(`.timeline-marker[data-id="${this.draggedMarker.id || this.draggedMarker.tempId}"]`);
    if (markerElement) {
      // ใช้ WorkingDaysManager ถ้ามี
      if (this.timeline.workingDaysManager && this.timeline.workingDaysManager.initialized) {
        const dayMove = Math.round(deltaX / dayWidth);
        const originalDate = this.timeline.workingDaysManager.markerDayToDate(this.markerInitialDay);

        if (originalDate) {
          // คำนวณวันที่ใหม่โดยเพิ่ม/ลด dayMove จากวันที่เดิม
          const newDate = this.timeline.workingDaysManager.addDays(originalDate, dayMove);
          if (newDate) {
            const newDay = this.timeline.workingDaysManager.dateToMarkerDay(newDate);
            const newLeft = this.timeline.workingDaysManager.dateToPosition(newDate, dayWidth);

            // ตรวจสอบว่าอยู่ในช่วงที่ถูกต้อง
            const daysInMonth = this.getDaysInMonth();
            if (newDay >= 0 && newDay < daysInMonth) {
              this.draggedMarker.day = newDay;
              markerElement.dataset.day = newDay;
              markerElement.style.left = `${newLeft}px`;
            }
          }
        }
      } else {
        const dayMove = Math.round(deltaX / dayWidth);
        let newDay = this.markerInitialDay + dayMove;
        const daysInMonth = this.getDaysInMonth();
        if (newDay < 0) newDay = 0;
        if (newDay >= daysInMonth) newDay = daysInMonth - 1;

        const newLeft = newDay * dayWidth;
        this.draggedMarker.day = newDay;
        markerElement.dataset.day = newDay;
        markerElement.style.left = `${newLeft}px`;
      }

      if (!this.timeline.options.lockMarkerToTopRow) {
        const taskRows = document.querySelectorAll('.task-row');
        if (taskRows.length > 0) {
          const deltaY = e.clientY - this.startY;
          const rowHeight = taskRows[0].offsetHeight;
          const rowMove = Math.round(deltaY / rowHeight);
          let newRowIndex = this.markerInitialRowIndex + rowMove;

          if (newRowIndex < 0) newRowIndex = 0;
          if (newRowIndex >= taskRows.length) newRowIndex = taskRows.length - 1;

          const diamond = markerElement.querySelector('.marker-diamond');
          if (diamond) {
            const timelineRect = this.timeline.timelineContainer.getBoundingClientRect();
            const groupHeaderHeight = this.timeline.groupHeaderHeight || 30;

            const rowRect = taskRows[newRowIndex].getBoundingClientRect();
            const middleRow = (rowRect.height / 2);

            // คำนวณตำแหน่งสัมพัทธ์โดยคำนึงถึงตำแหน่งของ tasks-area และความสูงของ group header
            const relativeTop = rowRect.top - timelineRect.top - groupHeaderHeight + middleRow - 6;
            diamond.style.top = `${Math.max(5, relativeTop)}px`;
          }

          this.draggedMarker.rowIndex = newRowIndex;
          markerElement.dataset.rowIndex = newRowIndex;
        }
      }
    }
  }

  /**
   * Revert marker to original position
   */
  async revertMarkerPosition(marker) {
    if (!this.initialMarkerPosition) return;

    // Restore original position
    marker.day = this.initialMarkerPosition.day;
    marker.date = this.initialMarkerPosition.date;
    marker.rowIndex = this.initialMarkerPosition.rowIndex;

    // Re-render marker
    this.renderMarker(marker);
  }

  /**
   * Get event coordinates from mouse or touch event
   */
  getEventCoordinates(e, inputType) {
    if (inputType === 'touch') {
      const touch = e.touches[0] || e.changedTouches[0];
      return {
        clientX: touch.clientX,
        clientY: touch.clientY
      };
    } else {
      return {
        clientX: e.clientX,
        clientY: e.clientY
      };
    }
  }

  /**
   * Enhanced get days in month with WorkingDaysManager support
   */
  getDaysInMonth() {
    if (this.timeline.headers && this.timeline.headers.length > 0) {
      return this.timeline.headers.length;
    }

    // Fallback calculation
    const year = this.timeline.currentDate.getFullYear();
    const month = this.timeline.currentDate.getMonth();
    return new Date(year, month + 1, 0).getDate();
  }

  /**
   * Show Marker's tooltip
   * @param {Event} e - Mouse event
   * @param {Object} marker - Marker information
   */
  showMarkerTooltip(e, marker) {
    // ใช้ WorkingDaysManager เพื่อดึงวันที่จาก marker day
    let dateString;

    if (this.timeline.workingDaysManager && this.timeline.workingDaysManager.initialized) {
      const date = this.timeline.workingDaysManager.markerDayToDate(marker.day);
      if (date) {
        const dateObj = new Date(date);
        dateString = this.timeline.t('{day} {month} {year}', {
          day: dateObj.getDate(),
          month: this.timeline.monthNames[dateObj.getMonth()],
          year: dateObj.getFullYear()
        });
      } else {
        dateString = this.timeline.t('Day {day}', {day: marker.day});
      }
    } else {
      dateString = this.timeline.t('Day {day}', {day: marker.day});
    }

    const tooltipText = `
      <strong>${this.timeline.t('Date')}:</strong> ${dateString}
      ${marker.name ? `<br><strong>${this.timeline.t('Name')}:</strong> ${this.timeline.escapeHtml(marker.name)}` : ''}
      ${marker.description ? `<br><strong>${this.timeline.t('Description')}:</strong> ${this.timeline.escapeHtml(marker.description)}` : ''}
    `;

    this.timeline.domRenderer.showTooltip(e, tooltipText);
  }

  /**
   * เพิ่มฟังก์ชันสำหรับส่ง marker ไปยัง API แบบทีละรายการ
   * @param {Object} marker - ข้อมูล marker ที่ต้องการส่ง
   * @returns {Promise<Object>} - ผลลัพธ์จาก API
   */
  async sendMarkerToAPI(marker) {
    const markerId = marker.id || marker.tempId;

    // ตรวจสอบว่าสามารถส่งข้อมูลได้หรือไม่
    if (!this.timeline._canUpdateMarker(markerId)) {
      return {success: false, error: 'Too frequent updates'};
    }

    // บันทึกว่ากำลังเริ่มส่งข้อมูล
    this.timeline._recordMarkerUpdate(markerId, true);

    try {
      if (!this.timeline.currentProject) {
        // บันทึกว่าสิ้นสุดการส่งข้อมูล
        this.timeline._recordMarkerUpdate(markerId, false);
        return {success: false, message: 'No active project'};
      }

      if (window.apiHandler) {
        const result = await window.apiHandler.saveMarker(marker);

        // บันทึกว่าสิ้นสุดการส่งข้อมูล
        this.timeline._recordMarkerUpdate(markerId, false);

        if (result && result.success) {
          // อัปเดท marker ID หรือข้อมูลอื่นๆ ที่ได้รับกลับมา
          if (result.data && result.data.data.marker) {
            const oldTempId = marker.tempId;
            marker.id = result.data.data.marker.id;

            // Remove old marker element
            const oldMarkerElement = document.querySelector(`.timeline-marker[data-id="${oldTempId}"]`);
            if (oldMarkerElement) {
              oldMarkerElement.remove();
            }

            // Re-render marker with new ID and proper event listeners
            this.renderMarker(marker);

            console.log('Marker ID updated and re-rendered (window.apiHandler):', oldTempId, '->', marker.id);
          }

          this.hasNewMarkers = false;
          return result;
        } else {
          console.error('Failed to update marker on server:', marker.tempId);
          return {success: false, error: result?.error || 'Unknown error'};
        }
      } else {
        // บันทึกว่าสิ้นสุดการส่งข้อมูล
        this.timeline._recordMarkerUpdate(markerId, false);

        return {success: false, error: 'No API handler available'};
      }
    } catch (error) {
      console.error('Error sending marker to API:', error);

      // บันทึกว่าสิ้นสุดการส่งข้อมูล
      this.timeline._recordMarkerUpdate(markerId, false);

      return {success: false, error: error.message};
    }
  }

  async deleteMarker(markerId) {
    console.log('deleteMarker called with markerId:', markerId);

    const result = await this.deleteMarkerAPI(markerId);
    console.log('deleteMarkerAPI result:', result);

    if (result && result.success) {
      // ลบ marker ออกจาก data array
      const originalLength = this.timeline.dataManager.markers.length;
      this.timeline.dataManager.markers = this.timeline.dataManager.markers.filter(m => m.id != markerId && m.tempId != markerId);
      const newLength = this.timeline.dataManager.markers.length;

      console.log('Markers filtered:', originalLength, '->', newLength);

      // Re-render markers
      this.renderMarkers();
      this.timeline.triggerDataChangeCallback();

      this.timeline.showNotification('Already deleted Marker', 'success');
    } else {
      console.error('Failed to delete marker:', result);
      this.timeline.showNotification('Unable to delete Marker: ' + (result?.error || 'Unknown error'), 'error');
    }
  }

  /**
   * ลบ marker โดยส่งคำสั่งไปยัง API โดยตรง
   * @param {string|number} markerId - รหัส marker ที่ต้องการลบ
   * @returns {Promise<Object>} - ผลลัพธ์จาก API
   */
  async deleteMarkerAPI(markerId) {
    console.log('deleteMarkerAPI called with:', markerId);

    if (!this.timeline.currentProject) {
      console.error('No active project');
      return {success: false, message: 'No active project'};
    }

    try {
      const data = {
        projectId: this.timeline.currentProject.id,
        markerId: markerId
      };

      console.log('Sending delete request with data:', data);
      const result = await window.apiHandler.sendRequest('delete_marker', 'POST', data);
      console.log('API delete response:', result);

      return result;
    } catch (error) {
      console.error('Error in deleteMarkerAPI:', error);
      return {success: false, error: error.message};
    }
  }

  /**
   * Resets the flag indicating the presence of new markers.
   * Sets the hasNewMarkers property to false.
   */
  resetNewMarkersFlag() {
    this.hasNewMarkers = false;
  }
}

window.MarkerManager = MarkerManager;
